import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  type SelectProps,
} from '@mui/material';
import { memo, useCallback } from 'react';

import type { ControlProps } from './SettingControl';

type T = ControlProps<string | string[]>;

export interface SelectControlProps<K extends string = string>
  extends
    ControlProps<string | string[]>,
    Omit<SelectProps, keyof T | 'onChange'> {
  type: 'select';
  options?: { value: string; label: string }[];
  allowAllOption?: boolean;
  allowEmptyOption?: boolean;
  allowNoneOption?: boolean;
  dependentExclusion?: K;
  exclusionMode?: 'exclude-current' | 'exclude-related';
  onChange?: (value: string | string[]) => void;
}

export const SelectControl = memo<Omit<SelectControlProps, 'type'>>(props => {
  const { name, label, value, options = [], ...rest } = props;
  // Auto-detect multi-select if not explicitly set
  const isMultiple = props.multiple || Array.isArray(props.value);

  const handleChange = useCallback(
    (event: SelectChangeEvent<string | string[]>) => {
      const newValue = event.target.value;

      if (isMultiple) {
        // For multiple selection, value should be an array
        const arrayValue: string[] = Array.isArray(newValue)
          ? newValue
          : [newValue];
        // Use setTimeout to defer the onChange call and prevent scroll issues
        setTimeout(() => props.onChange?.(arrayValue), 0);
      } else {
        // For single selection, value should be a string or number
        const singleValue = Array.isArray(newValue) ? newValue[0] : newValue;
        // Convert to number if the option value is a number
        const matchingOption = options.find(
          opt => String(opt.value) === singleValue,
        );
        const finalValue = matchingOption ? matchingOption.value : singleValue;
        // Use setTimeout to defer the onChange call and prevent scroll issues
        setTimeout(() => props.onChange?.(finalValue), 0);
      }
    },
    [props, isMultiple, options],
  );

  // Normalize value for display
  const displayValue = isMultiple
    ? Array.isArray(value)
      ? value.map(String)
      : value !== null && value !== undefined
        ? [String(value)]
        : []
    : String(value ?? '');

  return (
    <FormControl fullWidth>
      <InputLabel>{label || name}</InputLabel>
      <Select
        {...rest}
        multiple={isMultiple}
        value={displayValue}
        onChange={handleChange}
        label={label || name}
        variant="outlined"
        MenuProps={{
          disableRestoreFocus: true,
          PaperProps: {
            style: {
              maxHeight: 500,
            },
          },
        }}
        renderValue={
          isMultiple
            ? selected => {
                if (!Array.isArray(selected)) {
                  return String(selected);
                }
                return selected
                  .map(
                    val =>
                      options.find(opt => String(opt.value) === val)?.label ||
                      val,
                  )
                  .join(', ');
              }
            : undefined
        }
      >
        {options.map(option => (
          <MenuItem key={option.value} value={String(option.value)}>
            {option.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
});

SelectControl.displayName = 'SelectControl';
